'use client';

import { getFavoritesCount } from '@/lib/FavoritesStorage';
import { getIsAnonymous } from '@/lib/LocalStorage';
import { WebLink } from '@/lib/WebLinks';
import { WindowDispatchEvent } from '@/lib/WindowDispatchEvent';
import { MenuCategory } from '@/models/MenuCategories';
import { ArrowLeft, ChevronRight, Heart, User } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import OrderTrackViewEntry from './OrderTrackViewEntry';
import SmartImage from './SmartImage';
import { Button } from './ui/button';
import ReferralButton from './ui/referral-button';
import { ScrollArea } from './ui/scroll-area';
import Image from 'next/image';

export default function SidebarContent({
  setIsSidebarOpen,
  menuCategories,
  isApp,
}: {
  setIsSidebarOpen?: (isOpen: boolean) => void | undefined;
  menuCategories: MenuCategory[];
  isApp?: boolean;
}) {
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [visibleCategories, setVisibleCategories] = useState<MenuCategory[]>([]);
  const [isUserStatusSet, setIsUserStatusSet] = useState(false);

  useEffect(() => {
    const anonymousStateListener = () => {
      setIsAnonymous(getIsAnonymous());
      setIsUserStatusSet(true);
    };

    window.addEventListener(WindowDispatchEvent.UPDATE_ANONYMOUS_STATE, anonymousStateListener);
    setIsAnonymous(getIsAnonymous());
    setIsUserStatusSet(true);

    return () => {
      window.removeEventListener(
        WindowDispatchEvent.UPDATE_ANONYMOUS_STATE,
        anonymousStateListener,
      );
    };
  }, []);

  const setIsOpen = (isOpen: boolean) => {
    if (setIsSidebarOpen) {
      setIsSidebarOpen(isOpen);
    }
  };

  return (
    <div className="h-full relative">
      {visibleCategories.map((category) => (
        <div
          key={category.id}
          className="absolute top-0 left-0 w-full h-full bg-hzc_purple_500 animate-in fade-in-20 duration-300 flex flex-col z-10"
        >
          <ScrollArea className="flex-1">
            <SubcategoryList
              category={category}
              setIsSidebarOpen={setIsOpen}
              setVisibleCategories={setVisibleCategories}
            />
          </ScrollArea>
          <div className="mt-auto">
            <Link
              href={`/${category.titleWD}`}
              onClick={() => setIsOpen(false)}
              className="w-full bg-hzc_purple_500 text-[#D2F25C] text-[12px] font-medium px-4 py-2 flex flex-row items-center gap-2"
            >
              Tüm Ürünleri Gör
              <ChevronRight className="size-4" />
            </Link>
          </div>
        </div>
      ))}
      <ScrollArea className="h-full">
        <div className="flex flex-col px-4 space-y-4">
          <div>
            {isUserStatusSet ? (
              isApp ? (
                <OrderTrackViewEntry isSidebar isApp />
              ) : (
                <div className="flex flex-col space-y-2">
                  <div className="flex flex-row space-x-4">
                    <Link
                      href={isAnonymous ? WebLink.LOGIN : WebLink.PROFILE}
                      onClick={() => setIsOpen(false)}
                      className="flex items-center space-x-2 px-4 h-[38px] rounded-full bg-[#5E6AFF1A] transition-all duration-300 w-1/2"
                    >
                      <User className="size-5 text-white" />
                      <span className="text-[12px] font-medium text-white line-clamp-1">
                        {isAnonymous ? 'Üyelik' : 'Profilim'}
                      </span>
                    </Link>

                    <OrderTrackViewEntry isSidebar />
                  </div>
                  {!isAnonymous && (
                    <Link
                      href={`${WebLink.PROFILE}?type=favorilerim`}
                      onClick={() => setIsOpen(false)}
                      className="relative flex items-center space-x-2 px-4 h-[38px] rounded-full bg-[#5E6AFF1A] transition-all duration-300 w-1/2"
                    >
                      <Heart className="size-5 text-white" />
                      <span className="text-[12px] font-medium text-white line-clamp-1">
                        Favorilerim
                      </span>
                      {getFavoritesCount() > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-hzc_green text-hzc_beige_100 text-[10px] text-center leading-4">
                          {getFavoritesCount()}
                        </span>
                      )}
                    </Link>
                  )}
                </div>
              )
            ) : (
              <div
                className="w-1/2 h-[38px] rounded-full bg-[#5E6AFF1A] relative overflow-hidden"
                role="status"
                aria-label="Yükleniyor"
              >
                {/* Yalın renk overlay: sadece opaklık değişiyor */}
                <div
                  className="absolute inset-0 bg-[#5E6AFF] animate-fadepulse will-change-[opacity]"
                  style={{ opacity: 0.18 }}
                />
              </div>
            )}
          </div>
          {/* Categories */}
          <div className="flex flex-col">
            <span className="text-[16px] font-medium text-[#B5B7D3] mb-2">Kategorileri Keşfet</span>
            <div className="grid grid-cols-3 gap-4">
              {menuCategories.map((category) => (
                <div key={`menuCat_${category.id}`} className="w-full">
                  {category.children && category.children.length > 0 ? (
                    <div
                      key={category.id}
                      onClick={() => setVisibleCategories((prev) => [...prev, category])}
                      className="flex flex-col items-center justify-center transition-all duration-300 aspect-square hover:cursor-pointer"
                    >
                      <div className="flex items-center justify-center mb-2 rounded-[16px] bg-[#30388D] w-full h-full border border-white border-opacity-[0.16]">
                        <div className="w-8 h-8 relative flex items-center justify-center">
                          <SmartImage
                            src={category.menuIconUrl || ''}
                            alt={category.title}
                            width={32}
                            height={32}
                            className="max-w-full max-h-full object-contain"
                            style={{
                              filter: 'invert(1)',
                              zIndex: visibleCategories.length > 0 ? -1 : 0,
                            }}
                            imageType="icon"
                            unoptimized
                          />
                        </div>
                      </div>
                      <span className="text-[12px] font-normal text-white text-center line-clamp-2">
                        {category.title}
                      </span>
                    </div>
                  ) : (
                    <Link
                      key={category.id}
                      onClick={() => setIsOpen(false)}
                      href={`/${category.titleWD}`}
                      className="flex flex-col items-center justify-center transition-all duration-300 aspect-square"
                    >
                      <div className="flex items-center justify-center mb-2 rounded-[16px] bg-[#30388D] w-full h-full border border-white border-opacity-[0.16]">
                        <div className="w-8 h-8 relative flex items-center justify-center">
                          <SmartImage
                            src={category.menuIconUrl || ''}
                            alt={category.title}
                            width={32}
                            height={32}
                            className="max-w-full max-h-full object-contain"
                            style={{
                              filter: 'invert(1)',
                              zIndex: visibleCategories.length > 0 ? -1 : 0,
                            }}
                            imageType="icon"
                            unoptimized
                          />
                        </div>
                      </div>
                      <span className="text-[12px] font-normal text-white text-center line-clamp-2">
                        {category.title}
                      </span>
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>

          {!isApp && (
            <div className="w-full flex-col mb-12 flex md:hidden">
              <div className="text-l font-semibold tracking-tight text-slate-50">
                Hızlı Çiçek deneyimi artık cebinde!
              </div>
              <Link
                href="https://play.google.com/store/apps/details?id=com.hizlicicek.android"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Image
                  src="https://cdn.hizlicicek.com/public/app_store/android_hizli_cicek_indir.png"
                  alt="Google Play Store"
                  width={135}
                  height={40}
                  className="max-w-xs mt-2 w-[135px] h-[40px] object-contain"
                  unoptimized={true}
                />
              </Link>
              <Link
                href="https://apps.apple.com/ca/app/h%C4%B1zl%C4%B1-%C3%A7i%C3%A7ek-%C3%A7i%C3%A7ek-g%C3%B6nder/id6752895233"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Image
                  src="https://cdn.hizlicicek.com/public/app_store/apple_hizli_cicek_indir.png"
                  alt="Apple App Store"
                  width={135}
                  height={40}
                  className="max-w-xs mt-2 w-[135px] h-[40px] object-contain"
                  unoptimized={true}
                />
              </Link>
            </div>
          )}

          {/* Popular Categories */}
          <div className="flex flex-col">
            <span className="text-[16px] font-medium text-[#B5B7D3]">Popüler Kategoriler</span>
            <div className="flex flex-col">
              <Link
                href="/urunler/soz-nisan-dugun-cicekleri"
                onClick={() => setIsOpen(false)}
                className="py-3 text-[12px] font-normal text-white border-b border-[#FFFFFF] border-opacity-[0.1]"
              >
                Söz-Nişan-Düğün Çiçekleri
              </Link>
              <Link
                href="/urunler/gecmis-olsun-cicekleri"
                onClick={() => setIsOpen(false)}
                className="py-3 text-[12px] font-normal text-white  border-b border-[#FFFFFF] border-opacity-[0.1]"
              >
                Geçmiş Olsun Çiçekleri
              </Link>
              <Link
                href="/urunler/acilis-cicekleri"
                onClick={() => setIsOpen(false)}
                className="py-3 text-[12px] font-normal text-white  border-b border-[#FFFFFF] border-opacity-[0.1]"
              >
                Açılış Çiçekleri
              </Link>
              <Link
                href="/urunler/ozur-cicegi"
                onClick={() => setIsOpen(false)}
                className="py-3 text-[12px] font-normal text-white border-b border-[#FFFFFF] border-opacity-[0.1]"
              >
                Özür Çiçeği
              </Link>
              <Link
                href="/urunler/isteme-cicekleri"
                onClick={() => setIsOpen(false)}
                className="py-3 text-[12px] font-normal text-white border-b border-[#FFFFFF] border-opacity-[0.1]"
              >
                İsteme Çiçekleri
              </Link>
              <div className="py-3 text-[12px] font-normal text-white border-b border-[#FFFFFF] border-opacity-[0.1]">
                <ReferralButton className="menuReferralButton" onClick={() => setIsOpen(false)} />
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

function SubcategoryList({
  category,
  setIsSidebarOpen,
  setVisibleCategories,
}: {
  category: MenuCategory;
  setIsSidebarOpen: (isOpen: boolean) => void;
  setVisibleCategories: React.Dispatch<React.SetStateAction<MenuCategory[]>>;
}) {
  return (
    <div className="flex flex-col px-4">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-4 w-4 text-white"
          onClick={() => setVisibleCategories((prev) => prev.filter((c) => c.id !== category.id))}
        >
          <ArrowLeft className="size-4" />
        </Button>
        <span className="text-[16px] font-medium text-[#B5B7D3]">{category.title}</span>
      </div>
      {category.children?.map((subCategory) => (
        <div
          key={subCategory.id}
          className="flex items-center justify-between border-b border-[#FFFFFF] border-opacity-[0.1] hover:cursor-pointer"
        >
          {subCategory.children && subCategory.children.length > 0 ? (
            <div
              onClick={() => setVisibleCategories((prev) => [...prev, subCategory])}
              className="flex flex-row items-center justify-between gap-2 py-3 text-[12px] font-normal text-white w-full"
            >
              {subCategory.title}
              <ChevronRight className="size-4" />
            </div>
          ) : (
            <Link
              href={`/${subCategory.titleWD}`}
              onClick={() => setIsSidebarOpen(false)}
              className="flex-1 py-3 text-[12px] font-normal text-white"
            >
              {subCategory.title}
            </Link>
          )}
        </div>
      ))}
    </div>
  );
}
